import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
	ActivatedRoute,
	ActivatedRouteSnapshot,
	createUrlTreeFromSnapshot,
	UrlSerializer,
} from '@angular/router';
import {
	AuthnService,
	ComponentBase,
	DeploymentContext,
	UserService,
} from '@Common';

import {
	LocalizedTextIds,
	Mode,
	ReviewNotification,
} from 'company-finder-common';

@Component({
	selector: 'navigation-panel',
	templateUrl: './navigation-panel.component.html',
	styleUrls: ['./navigation-panel.component.scss'],
	standalone: false,
})
export class NavigationPanelComponent extends ComponentBase {
	@Input() public isFollowEnabled: boolean;
	@Input() public unseenEventCount: number;
	@Input() public updateCount: number;
	@Input() public reviews: ReviewNotification[];
	@Output() public goHome: EventEmitter<void> = new EventEmitter<void>();
	@Output() public goToCompanyUpdate: EventEmitter<number> =
		new EventEmitter<number>();
	@Output() public goToMyUpdates: EventEmitter<void> =
		new EventEmitter<void>();
	@Output() public goToPreferences: EventEmitter<number> =
		new EventEmitter<number>();
	@Output() public showPendingUpdates: EventEmitter<void> =
		new EventEmitter<void>();

	private snapshot: ActivatedRouteSnapshot;
	public constructor(
		dc: DeploymentContext,
		protected userService: UserService,
		protected authnService: AuthnService,
		private urlSerializer: UrlSerializer,
		route: ActivatedRoute
	) {
		super(dc);
		this.snapshot = route.snapshot;
	}

	public get isReviewer(): boolean {
		return this.userService.isReviewer;
	}

	public get isCompanyContact(): boolean {
		return this.userService.isCompanyContact;
	}

	public get isInternal(): boolean {
		return this.authnService.isInternal;
	}

	public get enableModeSwitching(): boolean {
		return this._deploymentContext.featureSwitches.enableModeSwitching;
	}

	public emitGoHome(): void {
		this.goHome.emit();
	}

	public emitGoToCompanyUpdate(index: number): void {
		this.goToCompanyUpdate.emit(index);
	}

	public emitGoToPreferences(): void {
		this.goToPreferences.emit();
	}

	public emitGoToMyUpdates(): void {
		this.goToMyUpdates.emit();
	}

	public emitShowPendingUpdates(): void {
		this.showPendingUpdates.emit();
	}

	public swapMode(): void {
		this.mode = this.inAssetMode ? Mode.Company : Mode.Asset;
	}

	public get exploreTitle(): string {
		return this.Loc(LocalizedTextIds.Explore);
	}

	public get followingTitle(): string {
		return this.Loc(LocalizedTextIds.Following);
	}

	public get preferencesTitle(): string {
		return this.Loc(LocalizedTextIds.Preferences);
	}

	public get companyProfileTitle(): string {
		return this.Loc(LocalizedTextIds.CompanyProfile);
	}
	public get reviewTitle(): string {
		return this.Loc(LocalizedTextIds.Review);
	}

	public isShowingMagicLinkForm: boolean = false;
	public isShowingMagicLinkResponse: boolean = false;
	public emailAddress: string = null;
	public magicLinkMessage: string = null;

	public get magicLinkButtonText(): string {
		return this.Loc(LocalizedTextIds.MagicLinkLoginText);
	}

	public get showMagicLinkButton(): boolean {
		return (
			this.featureSwitches.enableMagicLink &&
			!this.isAuthenticated &&
			!this.isShowingMagicLinkForm &&
			!this.isShowingMagicLinkResponse
		);
	}

	public dismissMagicLinkResponse(): void {
		this.isShowingMagicLinkResponse = false;
		this.magicLinkMessage = null;
	}

	public async sendMagicLink() {
		const url = createUrlTreeFromSnapshot(
			this.snapshot,
			['.'],
			this.snapshot.queryParams
		);
		const encoded = encodeURIComponent(this.urlSerializer.serialize(url));
		this.magicLinkMessage = await this.userService.sendMagicLink(
			this.emailAddress,
			encoded
		);
		this.isShowingMagicLinkForm = false;
		this.isShowingMagicLinkResponse = true;
		this.emailAddress = null;

		setTimeout(() => {
			this.dismissMagicLinkResponse();
		}, 5000);
	}

	public get magicLinkDescription(): string {
		return this.Loc(this.LocalizedTextIds.MagicLinkBlurb);
	}

	public showMagicLinkForm() {
		this.isShowingMagicLinkForm = true;
	}

	public get isAuthenticated(): boolean {
		return this.authnService.isAuthenticated;
	}
}
